import {isAllowed, updateConsent} from '$c3local/open-street-map.js';
import {Icon, LatLngBounds, Map as LeafletMap, Marker, TileLayer} from '$c3local/lib/leaflet.js';
import {viewTransition} from '$c3local/view-transition.js';

/**
 * @typedef {{link: string, title: string, coords: {lat: number, lng: number}, uid: number, marker: string}} MarkerData
 */

export function initReferenceList() {
    for (const container of document.querySelectorAll('.reference-list')) {
        /** @type {NodeListOf<HTMLDivElement>} */
        const items = container.querySelectorAll('.reference');
        /** @type {HTMLFormElement} */
        const filter = container.querySelector('.reference-list-filter');

        const mapContainer = container.querySelector('.reference-list-map');
        const consentContainer = mapContainer?.querySelector('.reference-list-map-consent');
        const mapViewport = container.querySelector('.reference-list-map-viewport');

        /** @type {Map<int, {marker: Marker, data: MarkerData}>} */
        const mapMarkers = new Map();
        /** @type {LeafletMap | null} */
        let map = null;

        /** @type {Map<HTMLDivElement, {categories: number[], title: string, uid: number}>} */
        const itemMap = new Map();

        for (const item of items) {
            itemMap.set(item, {
                uid: parseInt(item.dataset.uid),
                categories: JSON.parse(item.dataset.categories),
                title: item.innerText.toLowerCase(),
            });
        }

        const executeSearch = () => {
            const data = new FormData(filter);
            const category = parseInt(/** @type {string} */ data.get('category'));
            const search = data.get('search');

            /** @type {string} */
            const view = data.get('view') ?? 'list';

            if (view === 'map' && container.dataset.view !== view && map !== null) {
                setTimeout(() => {
                    map.invalidateSize(false);
                });
            }

            container.dataset.view = view;

            viewTransition(() => {
                let idx = 0;
                for (const item of items) {

                    const itemMeta = itemMap.get(item);

                    const categoryIncluded = category === 0 || itemMeta.categories.includes(category);
                    const searchIncluded = itemMeta.title.includes(search.toLowerCase());

                    const hidden = !(categoryIncluded && searchIncluded);
                    item.hidden = hidden;

                    const marker = mapMarkers.get(itemMeta.uid);

                    if (marker) {
                        if (hidden) {
                            map?.removeLayer(marker.marker);
                        } else {
                            map?.addLayer(marker.marker);
                        }
                    }

                    if (!hidden) {
                        idx++;
                    }
                }

                _fitBounds();
            });
        };

        filter?.addEventListener('change', executeSearch);
        filter?.addEventListener('submit', e => {
            e.preventDefault();
            executeSearch();
        });

        if (mapContainer !== null) {
            if (isAllowed()) {
                _initMap().catch(console.error);
            } else {
                const consentButton = consentContainer.querySelector('button');
                consentButton.addEventListener('click', () => {
                    updateConsent(true);
                    _initMap().catch(console.error);
                });
            }
        }

        async function _initMap() {
            consentContainer.remove();
            mapContainer.classList.add('reference-list-map--active');

            map = new LeafletMap(mapViewport);

            new TileLayer('https://tile.openstreetmap.de/{z}/{x}/{y}.png', {
                maxZoom: 19,
                attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>&nbsp;&nbsp;&nbsp;',
            }).addTo(map);

            /** @type {MarkerData[]} */
            const locationMarkers = JSON.parse(mapContainer.dataset.markers);
            for (const locationMarker of locationMarkers) {
                const marker = new Marker([locationMarker.coords.lat, locationMarker.coords.lng], {
                    title: locationMarker.title,
                    alt: locationMarker.title,
                    icon: createIcon(locationMarker.marker),
                });

                marker.addTo(map);

                /** @type {HTMLTemplateElement} */
                const popupContent = mapContainer.querySelector(`.reference-map-popup-content[data-uid="${locationMarker.uid}"]`);
                const popupElement = popupContent.content.cloneNode(true).firstElementChild;

                marker.bindPopup(popupElement, {
                    closeButton: false,
                    maxWidth: 384,
                });

                marker.on('click', () => {
                    for (const m of mapMarkers.values()) {
                        const isCurrent = m.marker === marker;
                        m.marker.setIcon(createIcon(m.data.marker, isCurrent));
                    }
                });

                mapMarkers.set(locationMarker.uid, {marker: marker, data: locationMarker});
            }

            _fitBounds();

            map.on('click', () => {
                for (const m of mapMarkers.values()) {
                    m.marker.setIcon(createIcon(m.data.marker, false));
                }
            });
        }

        function _fitBounds() {
            if (map === null) {
                return;
            }

            const bounds = new LatLngBounds();

            for (const marker of mapMarkers.values()) {
                if (!map.hasLayer(marker.marker)) {
                    continue;
                }

                const latLng = marker.marker.getLatLng();
                bounds.extend([latLng.lat, latLng.lng]);
            }

            map.fitBounds(bounds, {
                maxZoom: 14,
                padding: [2, 2],
            });
        }
    }
}

/**
 *
 * @param {string} url
 * @param {boolean} active
 * @returns {Icon}
 */
function createIcon(url, active = false) {
    return new Icon({
        iconUrl: url,
        iconSize: [40, 64],
        className: active ? 'leaflet-marker-icon--active' : '',
        // iconAnchor: [22, 94],
        // popupAnchor: [-3, -76],
    });
}
